import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Building, 
  Users, 
  Zap, 
  Home,
  Settings,
  Crown,
  MessageCircle,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  Plus,
  BookOpen,
  BarChart3
} from 'lucide-react';
import SubscriptionStatus from './SubscriptionStatus';
import WorkspaceSelector from './WorkspaceSelector';

const AppNavigation = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [investorCount, setInvestorCount] = useState(0);
  const [acceleratorCount, setAcceleratorCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsed') ?? 'false'); } catch { return false; }
  });

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
      return next;
    });
  };

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSidebarOpen && !event.target.closest('.sidebar')) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Fetch investor and accelerator counts for the selected workspace
  useEffect(() => {
    const fetchCounts = async () => {
      if (!currentUser || !selectedWorkspace || selectedWorkspace.isDemo) {
        setInvestorCount(0);
        setAcceleratorCount(0);
        return;
      }
      try {
        const [investorsSnap, acceleratorsSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'investors'),
            where('userId', '==', currentUser.uid),
            where('companyId', '==', selectedWorkspace.id)
          )),
          getDocs(query(
            collection(db, 'accelerators'),
            where('userId', '==', currentUser.uid),
            where('companyId', '==', selectedWorkspace.id)
          ))
        ]);
        setInvestorCount(investorsSnap.size);
        setAcceleratorCount(acceleratorsSnap.size);
      } catch (error) {
        console.error('Error fetching nav counts:', error);
        setInvestorCount(0);
        setAcceleratorCount(0);
      }
    };
    fetchCounts();
  }, [currentUser, selectedWorkspace?.id, selectedWorkspace?.isDemo]);

  const getCountForPath = (path) => {
    if (path === '/investor-tracker') return investorCount;
    if (path === '/accelerator-tracker') return acceleratorCount;
    return 0;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const getInitials = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return currentUser?.email?.[0].toUpperCase() || 'U';
  };

  const navigationItems = [
    {
      name: 'Pitch Deck',
      path: '/companies',
      icon: Building,
      description: 'Manage your pitch materials'
    },
    {
      name: 'Investors',
      path: '/investor-tracker',
      icon: Users,
      description: 'Track investor relationships & outreach'
    },
    {
      name: 'Accelerators',
      path: '/accelerator-tracker',
      icon: Zap,
      description: 'Manage accelerator applications & deadlines'
    }
  ];

  const resourceItems = [
    {
      name: 'Blog & Insights',
      path: '/blog',
      icon: BookOpen,
      description: 'Startup funding insights & guides'
    },
    {
      name: 'Top 100 Accelerators',
      path: '/blog/top-100-accelerators',
      icon: BarChart3,
      description: 'Latest 2025 accelerator rankings'
    }
  ];

  const isActiveRoute = (path) => {
    if (path === '/companies') {
      return location.pathname === '/companies';
    }
    if (path === '/investor-tracker') {
      return location.pathname.startsWith('/investor-tracker');
    }
    if (path === '/accelerator-tracker') {
      return location.pathname.startsWith('/accelerator-tracker');
    }
    return location.pathname.startsWith(path);
  };

  // Don't show navigation for certain routes
  const hideNavigationRoutes = [
    '/pitch-builder',
    '/settings',
    '/subscription',
    '/signin',
    '/signup',
    '/',
    '/realestate',
    '/demo-preview',
    '/onelinklanding',
    '/privacy-policy',
    '/dashboard'
  ];

  const shouldHideNavigation = hideNavigationRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  // Only show navigation for authenticated users
  if (!currentUser) {
    return <>{children}</>;
  }

  const handleQuickAction = (action) => {
    setShowQuickActions(false);
    if (action === 'create-pitch') {
      navigate('/companies');
    } else if (action === 'add-investor') {
      if (location.pathname !== '/investor-tracker') {
        navigate('/investor-tracker');
        setTimeout(() => {
          window.dispatchEvent(new Event('openAddInvestorModal'));
        }, 100);
      } else {
        window.dispatchEvent(new Event('openAddInvestorModal'));
      }
    } else if (action === 'add-accelerator') {
      if (location.pathname !== '/accelerator-tracker') {
        navigate('/accelerator-tracker');
        setTimeout(() => {
          window.dispatchEvent(new Event('openAddAcceleratorModal'));
        }, 100);
      } else {
        window.dispatchEvent(new Event('openAddAcceleratorModal'));
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex fixed inset-y-0 left-0 z-50">
        <div className={`flex flex-col bg-white shadow-xl transition-[width] duration-200 ease-out ${sidebarCollapsed ? 'w-20' : 'w-72'}`}>
          {/* Logo + collapse toggle */}
          <div className={`flex items-center border-b border-gray-200 ${sidebarCollapsed ? 'justify-center p-3' : 'justify-between p-6'}`}>
            <a href="/" className={`flex items-center hover:opacity-80 transition-opacity ${sidebarCollapsed ? 'flex-shrink-0' : 'gap-3'}`}>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <img src="/logo192.png" alt="DeckLink Logo" className="w-6 h-6" />
              </div>
              {!sidebarCollapsed && (
                <span className="text-xl font-light text-gray-900 whitespace-nowrap">
                  DeckLink<span className="text-blue-500">.co</span>
                </span>
              )}
            </a>
            {!sidebarCollapsed && (
              <button
                onClick={toggleSidebarCollapsed}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                title="Minimize sidebar"
                aria-label="Minimize sidebar"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Workspace Selector */}
          <div className={`border-b border-gray-200 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            <WorkspaceSelector collapsed={sidebarCollapsed} />
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-2 ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isActiveRoute(item.path);
              const count = getCountForPath(item.path);
              const btn = (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  title={sidebarCollapsed ? `${item.name}: ${item.description}${count > 0 ? ` (${count})` : ''}` : undefined}
                  className={`w-full flex items-center rounded-xl text-left transition-all group ${
                    sidebarCollapsed ? 'justify-center p-3' : 'gap-3 p-3'
                  } ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <IconComponent className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}`} />
                    {sidebarCollapsed && count > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold">
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          {item.name}
                          {count > 0 && (
                            <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold ${
                              isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'
                            }`}>
                              {count > 99 ? '99+' : count}
                            </span>
                          )}
                        </div>
                        <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                          {item.description}
                        </div>
                      </div>
                      {isActive && <ChevronRight className="w-4 h-4 text-blue-100 flex-shrink-0" />}
                    </>
                  )}
                </button>
              );
              return btn;
            })}
          </nav>

          {/* Workspace settings */}
          <div className={`border-t border-gray-200 ${sidebarCollapsed ? 'p-2' : 'px-4 pt-4'}`}>
            <button
              onClick={() => navigate('/workspace-settings')}
              title={sidebarCollapsed ? 'Workspace settings' : undefined}
              className={`w-full flex items-center rounded-xl text-left transition-all group ${
                sidebarCollapsed ? 'justify-center p-3' : 'gap-3 p-3'
              } ${
                location.pathname === '/workspace-settings'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Settings className={`w-5 h-5 flex-shrink-0 ${
                location.pathname === '/workspace-settings' ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'
              }`} />
              {!sidebarCollapsed && (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">Workspace settings</div>
                    <div className={`text-xs ${
                      location.pathname === '/workspace-settings' ? 'text-blue-100' : 'text-gray-500'
                    }`}>Name, logo, invite members</div>
                  </div>
                </>
              )}
            </button>
          </div>

          {/* Resources Section */}
          <div className={`pb-6 ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
            <div className="border-t border-gray-200 pt-4">
              {!sidebarCollapsed && (
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Resources</h3>
              )}
              <div className="space-y-2">
                {resourceItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = isActiveRoute(item.path);
                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.path)}
                      title={sidebarCollapsed ? `${item.name}: ${item.description}` : undefined}
                      className={`w-full flex items-center rounded-xl text-left transition-all group ${
                        sidebarCollapsed ? 'justify-center p-3' : 'gap-3 p-3'
                      } ${
                        isActive
                          ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {!sidebarCollapsed && (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{item.name}</div>
                            <div className={`text-xs ${isActive ? 'text-green-100' : 'text-gray-500'}`}>
                              {item.description}
                            </div>
                          </div>
                          {isActive && <ChevronRight className="w-4 h-4 text-green-100 flex-shrink-0" />}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Expand button when collapsed */}
            {sidebarCollapsed && (
              <button
                onClick={toggleSidebarCollapsed}
                className="w-full flex items-center justify-center p-3 mt-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                title="Expand sidebar"
                aria-label="Expand sidebar"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Top Navbar */}
      <div className={`hidden lg:block fixed top-0 right-0 z-40 transition-[left] duration-200 ease-out ${sidebarCollapsed ? 'left-20' : 'left-72'}`}>
        <div className="bg-white shadow-sm border-b border-gray-200 relative">
          <div className="flex items-center justify-between px-6 py-2">
            <div className="flex-1 flex justify-center">
              <button
                onClick={() => setShowQuickActions(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full px-6 py-2 shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium text-base"
                title="Quick Actions"
              >
                <Plus className="w-5 h-5" />
                Quick Actions
              </button>
            </div>
            {/* User Dropdown on the right */}
            <div className="flex items-center gap-4 ml-auto">
              <div className="relative user-dropdown">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-white">{getInitials()}</span>
                  </div>
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm text-gray-900 truncate">{currentUser?.email}</p>
                    </div>
                    <button
                      onClick={() => { navigate('/settings'); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={() => { navigate('/subscription'); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Crown className="w-4 h-4" />
                      Subscription
                    </button>
                    <button
                      onClick={() => { navigate('/companies'); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Home className="w-4 h-4" />
                      Overview
                    </button>
                    <button
                      onClick={() => { window.open(`sms:9848886717`, '_blank'); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Support
                    </button>
                    <button
                      onClick={() => { handleSignOut(); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <a href="/" className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center">
                <img src="/logo192.png" alt="DeckLink Logo" className="w-4 h-4" />
              </div>
              <span className="text-lg font-light text-gray-900">
                DeckLink<span className="text-blue-500">.co</span>
              </span>
            </a>
          </div>
          
          <div className="flex items-center gap-2">
            <SubscriptionStatus />
            <div className="relative user-dropdown">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{getInitials()}</span>
                </div>
              </button>
              
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm text-gray-900 truncate">{currentUser?.email}</p>
                  </div>
                  <button
                    onClick={() => { navigate('/settings'); setIsDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={() => { navigate('/subscription'); setIsDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    Subscription
                  </button>
                  <button
                    onClick={() => { navigate('/companies'); setIsDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Overview
                  </button>
                  <button
                    onClick={() => { window.open(`sms:9848886717`, '_blank'); setIsDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Support
                  </button>
                  <button
                    onClick={() => { handleSignOut(); setIsDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 sidebar"
          onClick={() => setIsSidebarOpen(false)}
        >
          <div
            className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl sidebar"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <a href="/" className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <img src="/logo192.png" alt="DeckLink Logo" className="w-6 h-6" />
                  </div>
                  <span className="text-xl font-light text-gray-900">
                    DeckLink<span className="text-blue-500">.co</span>
                  </span>
                </a>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
              
              {/* Workspace Selector */}
              <div className="p-4 border-b border-gray-200">
                <WorkspaceSelector />
              </div>
              {/* Navigation */}
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = isActiveRoute(item.path);
                  const count = getCountForPath(item.path);
                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <IconComponent className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          {item.name}
                          {count > 0 && (
                            <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold ${
                              isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                            }`}>
                              {count > 99 ? '99+' : count}
                            </span>
                          )}
                        </div>
                        <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>{item.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Workspace settings (mobile drawer) */}
              <div className="px-4 border-t border-gray-200 pt-4">
                <button
                  onClick={() => {
                    navigate('/workspace-settings');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                    location.pathname === '/workspace-settings'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Settings className={`w-5 h-5 flex-shrink-0 ${
                    location.pathname === '/workspace-settings' ? 'text-white' : 'text-gray-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">Workspace settings</div>
                    <div className={`text-xs ${
                      location.pathname === '/workspace-settings' ? 'text-blue-100' : 'text-gray-500'
                    }`}>Name, logo, invite members</div>
                  </div>
                </button>
              </div>

              {/* Resources Section */}
              <div className="px-4 pb-6">
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Resources</h3>
                  <div className="space-y-2">
                    {resourceItems.map((item) => {
                      const IconComponent = item.icon;
                      const isActive = isActiveRoute(item.path);
                      return (
                        <button
                          key={item.name}
                          onClick={() => navigate(item.path)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <IconComponent className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{item.name}</div>
                            <div className={`text-xs ${isActive ? 'text-green-100' : 'text-gray-500'}`}>{item.description}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Quick Actions FAB - bottom right, above the nav bar */}
      <div className="lg:hidden fixed bottom-20 right-4 z-50 safe-area-pb">
        <button
          onClick={() => setShowQuickActions(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full p-4 shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all border-4 border-white flex items-center justify-center"
          style={{ boxShadow: '0 4px 20px rgba(80, 63, 205, 0.35)' }}
          title="Quick Actions"
          aria-label="Quick Actions"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Bottom Navigation - 3 items only */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-pb" style={{ minHeight: '64px' }}>
        <div className="flex items-end justify-around py-2 px-1">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = isActiveRoute(item.path);
            const count = getCountForPath(item.path);
            return (
              <button
                key={item.name}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center py-2 px-2 flex-1 min-w-0 transition-colors ${
                  isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="relative">
                  <IconComponent className={`w-5 h-5 mb-1 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                  {count > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium leading-tight truncate w-full text-center">{item.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Actions Modal (shared) */}
      {showQuickActions && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQuickActions(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Quick Actions</h3>
              <button 
                onClick={() => setShowQuickActions(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
             
              <button
                onClick={() => handleQuickAction('add-investor')}
                className="w-full flex items-center p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-xl hover:from-green-100 hover:to-teal-100 transition-all duration-200 group"
              >
                <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-full p-3 mr-4 group-hover:scale-110 transition-transform">
                  <Users className="text-white" size={20} />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-900">Add Investor</h4>
                  <p className="text-sm text-gray-600">Expand your investor network</p>
                </div>
              </button>
              <button
                onClick={() => handleQuickAction('add-accelerator')}
                className="w-full flex items-center p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl hover:from-orange-100 hover:to-red-100 transition-all duration-200 group"
              >
                <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-full p-3 mr-4 group-hover:scale-110 transition-transform">
                  <Zap className="text-white" size={20} />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-900">Add Accelerator</h4>
                  <p className="text-sm text-gray-600">Track accelerator applications</p>
                </div>
              </button>
              <button
                onClick={() => handleQuickAction('create-pitch')}
                className="w-full flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:from-blue-100 hover:to-purple-100 transition-all duration-200 group"
              >
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-3 mr-4 group-hover:scale-110 transition-transform">
                  <Plus className="text-white" size={20} />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-900">Create New Pitch</h4>
                  <p className="text-sm text-gray-600">Start building your next fundraising deck</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'} lg:pt-12 transition-[margin] duration-200 ease-out`}>
        {children}
      </div>
    </div>
  );
};

export default AppNavigation; 